import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router'
import apiFetch from '../../../api/apiFetch'
import styles from '../css/HcpRow.module.css'
import ChangeTargetV2 from './ChangeTargetV2'
import { deriveRowStatus } from '../targetingUtils'

const STATUS_LABELS = {
    added:     'Newly added',
    dropped:   'Dropped',
    promoted:  'Promoted ↑',
    demoted:   'Demoted ↓',
    unchanged: 'Unchanged',
}

function TypeChip({ value }) {
    if (!value || value === 'NT') return <span className={[styles.chip, styles.chipNT].join(' ')}>NT</span>
    return <span className={[styles.chip, styles[`chip${value}`]].join(' ')}>{value}</span>
}

export default function HcpRow({ row, exercise, user, terr, lock, options, getTotals, onRowUpdate, onColumnReset, orderedColDefs, recentlyTouched, baselineRow, exerciseStatus, allowDIR, allowMOT, prevCol = 'PREV_TARGET_TYPE', prevFallbackCol = null }) {
    const rowStatus = deriveRowStatus(row, prevCol, prevFallbackCol)
    const rowStatusLabel = STATUS_LABELS[rowStatus]
    const proposedType = row.CURRENT_TARGET_VALUE
    const finalType = row.TARGET_TYPE || row.CHANGED_TARGET_VALUE || row.CURRENT_TARGET_VALUE
    const isFinalNT = !finalType || finalType === 'NT'
    const isDropped = rowStatus === 'dropped'
    // Highlight rows touched this session that have reverted to unchanged
    const isRecentlyReverted = rowStatus === 'unchanged' && recentlyTouched?.has(row.PROF_ID)

    // Auto-zero NumberEdit columns when the target transitions to NT
    const prevIsFinalNTRef = useRef(isFinalNT)
    useEffect(() => {
        const wasNT = prevIsFinalNTRef.current
        prevIsFinalNTRef.current = isFinalNT
        if (isFinalNT && !wasNT) {
            orderedColDefs?.forEach(def => {
                if (def.options === 'NumberEdit' && row[def.col]) {
                    updateCustomColumn(def.col, '0')
                }
            })
        }
    }, [isFinalNT])

    const name = [row.FIRST_NAME, row.MIDDLE_NAME, row.LAST_NAME].filter(Boolean).join(' ')
    const address = [row.ADDRESS, row.CITY, row.STATE, row.ZIP_CODE].filter(Boolean).join(', ')

    const updateCustomColumn = (key, value, isBaselineReset = false) => {
        apiFetch('/api/v2/targeting/update-custom-columns', {
            targeting_id: exercise?.TARGETING_EXERCISE_ID,
            source_id: row.SOURCE_ID,
            terr_id: terr,
            status_id: exerciseStatus?.STATUS_ID,
            mdmID: row.PROF_ID,
            key,
            value
        }).then(() => {
            if (getTotals) getTotals()
            if (onRowUpdate) onRowUpdate({ ...row, [key]: value })
            if (isBaselineReset && onColumnReset) onColumnReset(row.PROF_ID)
        })
    }

    const saveCallCount = (finalCount, initialCount) => {
        apiFetch('/api/v2/targeting/save-call-count', {
            TargetingId:  exercise?.TARGETING_EXERCISE_ID,
            TerrId:       terr,
            MdmId:        row.PROF_ID,
            InitialCount: Number(initialCount) || 0,
            FinalCount:   Number(finalCount)   || 0,
        })
    }

    const renderCell = (def) => {
        const cellStyle = def.width ? { width: def.width } : undefined
        if (def.col === '_status') return (
            <td key={def.id} className={[styles.statusLabel, styles[`status_${rowStatus}`]].join(' ')} style={cellStyle} title={rowStatusLabel}>
                {rowStatusLabel}
            </td>
        )
        if (def.col === '_action_reason') return (
            <td key={def.id} className={styles.actionReasonCell} style={cellStyle} title={row.REASON || undefined}>
                {row.REASON || ''}
            </td>
        )
        if (def.col === '_action') return (
            <td key={def.id} className={styles.action} style={cellStyle}>
                <ChangeTargetV2
                    row={row}
                    exercise={exercise}
                    user={user}
                    terr={terr}
                    lock={lock}
                    options={options}
                    getTotals={getTotals}
                    onRowUpdate={onRowUpdate}
                    allowDIR={allowDIR}
                    allowMOT={allowMOT}
                />
            </td>
        )
        if (def.col === 'Multiple') return (
            <td key={def.id} className={styles.hcp} style={cellStyle} title={address ? `${name} · ${address}` : name}>
                <Link to={`/hcp/details/${row.PROF_ID}`} className={styles.nameLink}>
                    <span className={isDropped ? styles.strikethrough : ''}>{name}</span>
                </Link>
                {address && <div className={styles.address}>{address}</div>}
            </td>
        )
        if (def.col === 'SPEC1') return (
            <td key={def.id} className={styles.specialty} style={cellStyle} title={row.SPEC1 || undefined}>{row.SPEC1}</td>
        )
        if (def.col === 'PREV_TARGET_TYPE') return (
            <td key={def.id} className={styles.center} style={cellStyle}><TypeChip value={row.PREV_TARGET_TYPE} /></td>
        )
        if (def.col === 'CURRENT_TARGET_VALUE') return (
            <td key={def.id} className={styles.center} style={cellStyle}><TypeChip value={proposedType} /></td>
        )
        if (def.col === 'CHANGED_TARGET_VALUE') return (
            <td key={def.id} className={styles.center} style={cellStyle}><TypeChip value={finalType} /></td>
        )
        // Any indication-specific target type column (e.g. DLBCL_TARGET_TYPE, FL_TARGET_TYPE,
        // SCAC_TARGET_TYPE) gets chip rendering with NT fallback for blank values.
        // PREV_TARGET_TYPE is already handled above and won't reach this branch.
        if (def.col.endsWith('_TARGET_TYPE')) return (
            <td key={def.id} className={styles.center} style={cellStyle}><TypeChip value={row[def.col]} /></td>
        )
        if (def.col === 'NPI') return (
            <td key={def.id} className={styles.npiCell} style={cellStyle} title={String(row.NPI ?? '')}>{row.NPI ?? ''}</td>
        )
        if (def.col === 'LAST_DIRECT_CALL') {
            const raw = row.LAST_DIRECT_CALL
            let display = ''
            if (raw) {
                // Parse ISO date parts directly to avoid UTC→local timezone shift
                const parts = String(raw).split('T')[0].split('-')
                if (parts.length === 3) {
                    display = `${parseInt(parts[1])}/${parseInt(parts[2])}/${parts[0].slice(2)}`
                } else {
                    display = raw
                }
            }
            return <td key={def.id} className={styles.dateCell} style={cellStyle} title={display || undefined}>{display}</td>
        }
        const val = row[def.col] ?? ''
        if (def.options === 'NumberEdit') {
            const originalVal = String(baselineRow?.[def.col] ?? '')
            // When the row is NT the field is disabled; show 0 immediately rather than
            // the stale value from before the downgrade.  Also key on `isFinalNT` so the
            // uncontrolled input remounts when NT is toggled — ensuring the restored
            // value reflects the DB state (0) rather than the old DOM value.
            const displayVal = isFinalNT ? '0' : val
            return (
                <td key={def.id} className={styles.extraCell} style={cellStyle}>
                    <input
                        key={`${def.col}-${isFinalNT ? 'nt' : val}`}
                        type="number"
                        min="0"
                        className={styles.numInput}
                        defaultValue={displayVal}
                        onBlur={e => {
                            const newVal = e.target.value
                            updateCustomColumn(def.col, newVal, newVal === originalVal)
                            saveCallCount(newVal, originalVal)
                        }}
                        disabled={lock || isFinalNT}
                    />
                </td>
            )
        }
        const strVal = String(val)
        const isLong = strVal.length > 50
        return (
            <td key={def.id} className={isLong ? styles.clampedCell : styles.extraCell} title={strVal || undefined} style={cellStyle}>
                {isLong ? <div className={styles.clampedInner}>{strVal}</div> : strVal}
            </td>
        )
    }

    return (
        <tr className={[styles.row, styles[rowStatus], isRecentlyReverted ? styles.recentlyReverted : ''].join(' ')}>
            {orderedColDefs.map(renderCell)}
        </tr>
    )
}
